/* eslint-disable max-len */
import React from 'react'
import { useIntl } from 'react-intl'

import { calculatePartDayHours, computeDaysAndHours, getFormattedDaysAndHours, getRawDaysOrHours } from '../../functions/get-days-or-hours'

import { IDisplayLeaveDuration, IDisplayLeaveDurationBots, IDisplayLeaveInDaysAndHoursBots, LeaveDisplayFormatEnum } from '../../types/display-in-days-hours'
import { IToilRequest } from '../../types/toil-request'
import { DEFAULT_WORKING_HOURS_IN_DAY } from '../../data/app-parameters'
import { getAverageWorkingHours, userHasWorkingHoursSchedule } from '../../functions/work-week'

export const DisplayLeaveDuration = ({
  hourlyLeaveAccounting = false,
  value,
  format = LeaveDisplayFormatEnum.long,
  daysList,
  hasSchedule,
}: IDisplayLeaveDuration): React.ReactElement => {

  const { formatMessage } = useIntl()
  if (value === 0) {
    return <>{value}</>
  }

  const totalLeaveHours = daysList?.reduce((acc, day) => acc + day.leaveHours, 0) || 0

  if (hasSchedule) {
    // We only care about raw hours, since its a schedule - put zeros for everything else
    const quota = { raw: { hours: totalLeaveHours, days: 0 }, days: 0, hours: totalLeaveHours, minutes: 0, isNegative: false }
    return <>{getRawDaysOrHours(quota, 'hours', formatMessage, LeaveDisplayFormatEnum.short)}</>
  }

  //new leaves will have daysList, old ones(8h) will not
  const numberOfWorkingHoursPerDay = daysList?.[0]?.workingHoursInDay || DEFAULT_WORKING_HOURS_IN_DAY

  const val = hourlyLeaveAccounting ? value : totalLeaveHours
  // We always want to display minutes in the leave duration (`true` argument in `computeDaysAndHours` function)
  // because the minutes are displayed only when they are not 0.
  // The minutes can be increments of 15 in case customers enable shortest leave intervals than 1 hours (15 or 30 minutes).
  const leave = computeDaysAndHours(val, numberOfWorkingHoursPerDay, true)

  return <>
    {getFormattedDaysAndHours({
      leave,
      formatMessage,
      displayFormat: format,
    })}
  </>
}

export const displayLeaveDuration = ({
  hourlyLeaveAccounting = false,
  value,
  format = LeaveDisplayFormatEnum.long,
  daysList,
  formatMessage,
}: IDisplayLeaveDurationBots) => {
  if (value === 0) {
    return `${value}`
  }
  //new leaves will have daysList, old ones(8h) will not
  const numberOfWorkingHoursPerDay = daysList?.[0]?.workingHoursInDay || DEFAULT_WORKING_HOURS_IN_DAY

  const val = hourlyLeaveAccounting ? value : value * numberOfWorkingHoursPerDay
  // We always want to display minutes in the leave duration (`true` argument in `computeDaysAndHours` function)
  // because the minutes are displayed only when they are not 0.
  // The minutes can be increments of 15 in case customers enable shortest leave intervals than 1 hours (15 or 30 minutes).
  const leave = computeDaysAndHours(val, numberOfWorkingHoursPerDay, true)
  return getFormattedDaysAndHours({
    leave,
    formatMessage,
    displayFormat: format,
  })
}

export const displayLeaveInDaysAndHours = ({
  hourlyLeaveAccounting = false,
  value,
  format = LeaveDisplayFormatEnum.long,
  formatMessage,
  workingHours,
}: IDisplayLeaveInDaysAndHoursBots) => {
  if (value === 0) {
    return `${value}`
  }

  const hasSchedule = userHasWorkingHoursSchedule(workingHours)
  if (hasSchedule) {
    // We only care about raw hours, since its a schedule - everything else can be zeros
    const quota = {
      raw: {
        hours: value,
        days: 0,
      },
      days: 0,
      hours: value,
      minutes: 0,
      isNegative: false,
    }
    // We know that user quota is set in hours when they have a working hours schedule
    return getRawDaysOrHours(quota, 'hours', formatMessage, LeaveDisplayFormatEnum.short)
  }

  // When there's no schedule, we get an average working hours per day (which is the same for every day in this case anyway)
  const numberOfWorkingHoursPerDay = getAverageWorkingHours(workingHours)

  const val = hourlyLeaveAccounting ? value : value * numberOfWorkingHoursPerDay
  const leave = computeDaysAndHours(val, numberOfWorkingHoursPerDay, true)
  return getFormattedDaysAndHours({
    leave,
    formatMessage,
    displayFormat: format,
  })
}

type ToilRequestDisplay = Pick<IToilRequest, 'isPartDay' | 'partDay' | 'partDayEndHour' | 'partDayStartHour' | 'daysList'>
export const displayToilInDaysAndHours = (formatMessage, toilRequest: ToilRequestDisplay) => {
  let amount
  if (toilRequest.isPartDay) {
    if (toilRequest.partDay) {
      amount = calculatePartDayHours(toilRequest.partDay)
    } else {
      amount = (toilRequest.partDayEndHour as number - Number(toilRequest.partDayStartHour) as number)
    }
  } else {
    amount = toilRequest.daysList.length
  }

  const unit = toilRequest.isPartDay
    ? formatMessage({ id: amount === 1 ? 'app.hour' : 'app.hours' })
    : formatMessage({ id: amount === 1 ? 'app.day' : 'app.days' })

  return `${amount} ${unit}`
}
